import {Alert, Card, Col, Icon, Row, Spin, Table} from "antd";
import Text from 'antd/lib/typography/Text';
import {cleanGraphQLErrorMsg} from 'common/util';
import withFlightTimesEntryDrawer from 'components/flights/times/WithFlightTimesEntryDrawer';
import withCognitoUser from 'components/WithCognitoUser';
import WithOrgData from "components/WithOrgData";
import React from 'react';
import {useQuery} from 'react-apollo';
import {compose} from 'redux';
import {useInfiniteScroll} from "hooks/infinite-scroller/infinite-scroller";
import {BookmarkAdapter} from "hooks/infinite-scroller/adapter/bookmark-adapter";
import {NetworkStatus} from "apollo-boost";
import CenteredLoadingScreen from "../../CenteredLoadingScreen";
import {PrintoutTimesButton} from "components/flights/times/PrintoutTimesButton";
import {FINALIZED_FLIGHTS} from './graphql';
import {PrintoutPercentageButton} from "components/flights/times/PrintoutPercentageButton";

const FlightTable = ({data, flightDrawer, refetch}, ...rest) => {
    const columns = [
        {
            title: 'Name',
            key: 'desig',
            render: (_, record) => {
                return `${record.desig}`
            },
            defaultSortOrder: 'descend',
            sorter: (a, b) => {
                if(a.desig.toLowerCase() > b.desig.toLowerCase()){
                    return -1;
                }
                if(a.desig.toLowerCase() < b.desig.toLowerCase()){
                    return 1;
                }
                return 0;
            },
        },
        {
            title: 'Tail No.',
            key: 'tailNum',
            render: (_, record) => record?.aircraftID?.tailNum
        },
        {
            title: 'Departure',
            key:'departure',
            render: (_, record) => {
                var legs = JSON.parse(record.legs);
                if('0' in legs){
                    return legs['0'].departure;
                }

                return 'None';
            },
            sorter: (a, b) => {
                var legsA = JSON.parse(a.legs);
                var legsB = JSON.parse(b.legs);

                var depA = "None";
                var depB = "None";
                if('0' in legsA){
                    depA = legsA['0'].departure;
                }
                if('0' in legsB){
                    depB = legsB['0'].departure;
                }

                if(depA.toLowerCase() > depB.toLowerCase()){
                    return -1;
                }
                if(depA.toLowerCase() < depB.toLowerCase()){
                    return 1;
                }
                return 0;
            },
        },
        {
            title: 'Pax Out',
            key:'paxOut',
            render: (_, record) => {
                var count = 0;
                record.paxIDList.forEach(pax => {
                    if(pax.transitType === "OUTBOUND"){
                        count+=1;
                    }
                })
                return count;
            }
        },
        {
            title: 'Pax In',
            key:'paxIn',
            render: (_, record) => {
                var count = 0;
                record.paxIDList.forEach(pax => {
                    if(pax.transitType === "INBOUND"){
                        count+=1;
                    }
                })
                return count;
            }
        },
        {
            title: 'Cgo Out',
            key:'cgoOut',
            render: (_, record) => {
                var count = 0;
                record.cgoIDList.forEach(cgo => {
                    if(cgo.transitType === "OUTBOUND"){
                        count+=1;
                    }
                })
                return count;
            }
        },
        {
            title: 'Cgo In',
            key:'cgoIn',
            render: (_, record) => {
                var count = 0;
                record.cgoIDList.forEach(cgo => {
                    if(cgo.transitType === "INBOUND"){
                        count+=1;
                    }
                })
                return count;
            }
        },
        {
            title: 'Seat %',
            key: 'seatPercent',
            render: (_, record) => {
                if (!record || !record.aircraftID){
                    return null
                }
                if(record.legsArray && record.legsArray[0] && record.aircraftID.maxSeats){
                    const seats = record.aircraftID.maxSeats || 0
                    const paxCount = record.legsArray[0].paxCount

                    const num = ((paxCount/seats)*100).toFixed(2)

                    if(num === 'NaN' || num === 'Infinity' || num < 0){
                        return null
                    }

                    return num
                }

                return null;
            }
        },
        {
            title: 'Weight %',
            key: 'weightPercent',
            render: (_, record) => {
                if (!record){
                    return null
                }
                if(record.legsArray && record.legsArray[0]){
                    const paxWeight = record.legsArray[0].paxWeight
                    const firstLegUsableWeight = record.firstLegUsableWt || 0
                    const aircraftMaxLoad = record?.aircraftID?.maxLoad || 0;

                    // If the user didn't enter a firstLegUsableWt, then use the aircraft maxLoad instead.
                    // From https://dataflyt.atlassian.net/browse/MCR-1116
                    let usefulLoad = firstLegUsableWeight;
                    if (firstLegUsableWeight <= 0){
                        usefulLoad = aircraftMaxLoad;
                    }

                    const num = ((paxWeight/usefulLoad)*100).toFixed(2)
                    
                    if(num === 'NaN' || num === 'Infinity' || num < 0){
                        return null
                    }
                    
                    return num
                }

                return null;
            }
        },
        {
            title: 'Flight Time',
            key:'flightTime',
            render: (_, record) => {
                var legs = record.legsArray;
                var noTimeEntered = legs.every(leg => leg.flightTime === null || leg.flightTime === 0);
                var paxNCgo = record.paxIDList.concat(record.cgoIDList)
                var noChargeCode = paxNCgo.length > 0 && paxNCgo.some(obj => !obj.chargeCode || obj.chargeCode === null)
                if(noTimeEntered){
                    return {
                        props: {
                            style: {background:'red', color: 'black'}
                        },
                        children: noChargeCode ? "Missing Charge Codes. No Time Entered." : "No Time Entered"
                    }
                }

                var legNoTime = legs.some(leg => leg.flightTime === null || leg.flightTime === 0);
                var time = 0;
                legs.forEach(leg => {
                    if(Number.isInteger(leg.flightTime)){
                        time += leg.flightTime;
                    }
                })
                if(legNoTime){
                    return {
                        props: {
                            style: {background: noChargeCode? 'red' : 'yellow', color: 'black'}
                        },
                        children: noChargeCode ? time + " | Leg With No Time. Missing Charge Codes" : time + " | Leg With No Time"
                    }
                }

                if(noChargeCode){
                    return {
                        props: {
                            style: {background:'red', color: 'black'}
                        },
                        children: "Missing Charge Codes"
                    }
                }

                return {
                    props: {
                        style: {background:'green', color:'white'}
                    },
                    children: time
                }
            }
        }
    ]
    return (
        <Table
            columns={columns}
            dataSource={data}
            rowKey={record => record._id}
            onRow={(record, rowIndex) => {
                return {
                    onClick: event => {
                        flightDrawer.open(record._id, refetch)
                    },
                    style: { cursor: 'pointer' }
                }
            }}
            pagination={false}
            size="middle"
            style={{marginBottom: '1rem'}}
            bordered
            footer={flightData => {
                var totalFlightTime = 0;
                flightData.forEach((flight) => {
                   flight.legsArray.forEach(leg => {
                        if(Number.isInteger(leg.flightTime)){
                            totalFlightTime += Number.parseInt(leg.flightTime)
                        }
                    })
                })
                return (<div>
                    <Text strong={true}>Total Daily Flight Time: </Text>
                    <Text style={{color:'yellow', fontWeight:"bold"}}>{totalFlightTime}</Text>
                  </div>);

            }}
            {...rest}
        />
    )
}

const DayGroupCard = ({data, flightDrawer, refetch}, ...rest ) => {
    return (
        <Card
            style={{marginBottom: '1rem'}}
            type="inner"
            title={<h3>{data[0]}</h3>}
            {...rest}
        >
            <FlightTable refetch={refetch} data={data[1]} flightDrawer={flightDrawer}/>
        </Card>
    )
}

const FlightTimes = (props) => {
    const queryResult = useQuery(FINALIZED_FLIGHTS,
    {
        variables: {
            tpID: props.orgData.transporter._id,
            customerID: props.orgData.customer._id,
            startDate: props.dateRange[0].format('YYYY-MM-DD'),
            endDate: props.dateRange[1].format('YYYY-MM-DD'),
            departureID: props.departureID && props.departureID.key
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    });

    const mangoAdapter = new BookmarkAdapter({
        query: FINALIZED_FLIGHTS,
        getResultFromResponse: (data) => data?.getFinalizedFlights,
        queryResult: queryResult
    });

    const { sentryRef, hasNextPage } = useInfiniteScroll({
        adapterImpl: mangoAdapter,
        rootMargin: '0px 0px 600px 0px'
    });

    const { data, networkStatus, error, refetch } = queryResult;

    const loading = networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables ||
        networkStatus === NetworkStatus.refetch;

    if (loading) return <CenteredLoadingScreen style={{ minHeight: '10rem' }} />

    const getFinalizedFlights = data?.getFinalizedFlights;

    var groups = {};

    if(getFinalizedFlights && getFinalizedFlights.docs && getFinalizedFlights.docs.length > 0){
        getFinalizedFlights.docs.forEach(currentFlight => {
            if(!(currentFlight.scheduledFlightDate in groups)){
                groups[currentFlight.scheduledFlightDate] = [currentFlight];
            }else{
                groups[currentFlight.scheduledFlightDate].push(currentFlight);
            }
        });

    }

    return (
        <div>
            {error ? <Alert
                    type="error"
                    style={{ marginTop: '12px' }}
                    message="Failed to display finalized flights."
                    description={String(error.message).includes('too large') ? '' + cleanGraphQLErrorMsg(error.message) + '. Please narrow your date range selection.' : null}
                    showIcon/>
                : null}
            {!error && Object.keys(groups).length === 0 ? "No Finalized Flights Found." : null}
            {!error && Object.keys(groups).length > 0 ? (
                <>
                    <Row gutter={12} style={{ padding: '12px 0', width: 'fit-content' }}>
                        <Col span={12}>
                            <PrintoutTimesButton {...props}/>
                        </Col>
                        <Col span={12}>
                            <PrintoutPercentageButton {...props}/>
                        </Col>
                    </Row>
                    {Object.entries(groups).map((group, i) => {
                        return <DayGroupCard refetch={refetch} data={group} key={i} flightDrawer={props.flightDrawer} />
                    })}
                    <div
                        className="sentryRef"
                        ref={sentryRef}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '7rem',
                            width: '100%'
                        }}
                    >
                        {queryResult.networkStatus === NetworkStatus.fetchMore && hasNextPage ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Spin size="large" indicator={<Icon type="loading" />} />
                                </div>
                                <div>Above data may be incomplete.</div>
                                <div>Please wait for next page.</div>
                            </div>
                        ) : null}
                        {queryResult.networkStatus === NetworkStatus.ready && !hasNextPage ? (
                            <div>End of results.</div>
                        ) : null}
                    </div>
                </>
            ) : null}

        </div>
    )
}


export default compose(
    WithOrgData,
    withCognitoUser,
    withFlightTimesEntryDrawer({editMode: true})
)(FlightTimes)